import { getAllFrames } from '@sketch/inspector'
import { useCallback, useContext, useMemo } from 'react'
import { ShareVersionContext } from '../../../versioning'
import { GridAndLayoutFrame, GridAndLayoutSetting } from './types'
import { useGridsAndLayoutsContext } from './GridsAndLayoutsContext'
// useInspectorContext needs to be imported like this to avoid circular dependencies
import { useInspectorContext } from '../../FrameView/hooks/inspector'
import { usePreview } from '../../views/PreviewView/PreviewContext'

const DEFAULT_GRID_AND_LAYOUT_SETTING: GridAndLayoutSetting = {
  isGridEnabled: false,
  isLayoutEnabled: false,
}

// MARK: Exported hooks

/**
 * Returns the list of frames in the current document that have a grid or layout settings enabled.
 * This is the list of frames for which we need to render the grid and layout overlays.
 */
export function useFramesWithGridAndLayout(): GridAndLayoutFrame[] {
  const gridsAndLayoutsForDoc = useGridsAndLayoutsForDocument()
  const gridAndLayoutByFrame = gridsAndLayoutsForDoc?.gridAndLayoutByFrame

  const hasFramesWithGridAndLayout =
    gridAndLayoutByFrame && Object.keys(gridAndLayoutByFrame).length > 0

  const allFrames = useAllFrames(hasFramesWithGridAndLayout)

  const framesWithGridAndLayout = useMemo(() => {
    if (!hasFramesWithGridAndLayout) {
      return []
    }

    return allFrames.reduce((acc: GridAndLayoutFrame[], frame) => {
      const gridAndLayoutForFrame = gridAndLayoutByFrame[frame.elementUUID]
      if (gridAndLayoutForFrame) {
        acc.push({
          frameId: frame.elementUUID,
          isGridEnabled: gridAndLayoutForFrame.isGridEnabled,
          isLayoutEnabled: gridAndLayoutForFrame.isLayoutEnabled,
          frameElement: frame,
        })
      }

      return acc
    }, [])
  }, [allFrames, gridAndLayoutByFrame, hasFramesWithGridAndLayout])

  return framesWithGridAndLayout
}

/**
 * Returns the grid and layout settings for a specific frame in the current document.
 */
export function useGridAndLayoutSettingsForFrame(frameId: string | undefined) {
  const gridsAndLayoutsForDoc = useGridsAndLayoutsForDocument()
  const frameSettings = frameId
    ? gridsAndLayoutsForDoc?.gridAndLayoutByFrame?.[frameId]
    : undefined

  return frameSettings ?? DEFAULT_GRID_AND_LAYOUT_SETTING
}

/**
 * Allow to toggle the isGridEnabled and isLayoutEnabled settings for a specific frame.
 */
export function useToggleGridSettingsForFrame(frameId: string | undefined) {
  const setFrameGridAndLayoutSetting = useSetFrameGridAndLayoutSetting()

  const toggleGrid = useCallback(() => {
    if (frameId === undefined || !setFrameGridAndLayoutSetting) return

    setFrameGridAndLayoutSetting(frameId, prev => ({
      ...prev,
      isGridEnabled: !prev.isGridEnabled,
    }))
  }, [frameId, setFrameGridAndLayoutSetting])

  const toggleLayout = useCallback(() => {
    if (frameId === undefined || !setFrameGridAndLayoutSetting) return

    setFrameGridAndLayoutSetting(frameId, prev => ({
      ...prev,
      isLayoutEnabled: !prev.isLayoutEnabled,
    }))
  }, [frameId, setFrameGridAndLayoutSetting])

  return useMemo(() => {
    return {
      toggleGrid,
      toggleLayout,
    }
  }, [toggleGrid, toggleLayout])
}

// MARK: Internal

/**
 * Returns the document identifier based on available context.
 * Checks ShareVersionContext first (for editing shares),
 * then PreviewContext (for preview views).
 */
function useDocumentIdentifier():
  | { identifier: string; type: 'share' | 'preview' }
  | undefined {
  const shareVersionContext = useContext(ShareVersionContext)
  const previewContext = usePreview()

  if (shareVersionContext) {
    return {
      identifier: shareVersionContext.share.identifier,
      type: 'share',
    }
  }

  if (previewContext) {
    return {
      identifier: previewContext.preview.identifier,
      type: 'preview',
    }
  }

  return undefined
}

/**
 * Returns the grid and layout settings data for the current document.
 */
function useGridsAndLayoutsForDocument() {
  const { gridsAndLayoutsByDocument } = useGridsAndLayoutsContext()
  const documentIdentifier = useDocumentIdentifier()

  if (!documentIdentifier) {
    return undefined
  }

  const gridsAndLayoutsForDoc =
    gridsAndLayoutsByDocument?.[documentIdentifier.identifier]

  return gridsAndLayoutsForDoc
}

/**
 * Set the grid and layout settings for a specific frame of the current document.
 */
function useSetFrameGridAndLayoutSetting() {
  const { gridsAndLayoutsByDocument, setGridsAndLayoutsByDocument } =
    useGridsAndLayoutsContext()

  const documentIdentifier = useDocumentIdentifier()
  const identifier = documentIdentifier?.identifier

  // Call useCallback unconditionally at the top level
  const setFrameGridAndLayoutSetting = useCallback(
    (
      frameId: string,
      getNewGridAndLayoutSettingForFrame: (
        prev: GridAndLayoutSetting
      ) => GridAndLayoutSetting
    ) => {
      // Early return if no identifier
      if (!identifier) return

      // useLocalStorage from react-use (used in GridsAndLayoutsProvider.tsx) has a bug that prevents us from passing a function
      // to update the state from the previous state. So we do it manually as a workaround.
      // https://github.com/streamich/react-use/issues/2560
      const prev = gridsAndLayoutsByDocument

      const prevGridAndLayoutByFrame = prev?.[identifier]?.gridAndLayoutByFrame
      const prevSettingsForFrame =
        prevGridAndLayoutByFrame?.[frameId] ?? DEFAULT_GRID_AND_LAYOUT_SETTING

      const newSettingForFrame =
        getNewGridAndLayoutSettingForFrame(prevSettingsForFrame)

      const newGridAndLayoutByFrame: Record<string, GridAndLayoutSetting> = {
        ...prevGridAndLayoutByFrame,
      }

      const isDefaultSetting =
        newSettingForFrame.isGridEnabled === false &&
        newSettingForFrame.isLayoutEnabled === false

      if (isDefaultSetting) {
        // If both grid and layout are disabled, we are back to the default and
        // we can remove the frame from the settings
        delete newGridAndLayoutByFrame[frameId]
      } else {
        // Otherwise, we update the settings for the frame
        newGridAndLayoutByFrame[frameId] = newSettingForFrame
      }

      const newState = { ...prev }

      if (Object.keys(newGridAndLayoutByFrame).length === 0) {
        // If there are no frames with grid and layout settings, we can remove the document entry
        delete newState[identifier]
      } else {
        // Otherwise, we update the document entry with the new settings
        newState[identifier] = {
          gridAndLayoutByFrame: newGridAndLayoutByFrame,
          lastUpdated: Date.now(),
        }
      }

      setGridsAndLayoutsByDocument(newState)
    },
    [gridsAndLayoutsByDocument, setGridsAndLayoutsByDocument, identifier]
  )

  if (!documentIdentifier) {
    return () => {}
  }

  return setFrameGridAndLayoutSetting
}

/**
 * Returns all frames (top or nested) in the current sketch scene.
 * The enabled parameter allows to disable the processing of frames until it is needed.
 * If this process ends up taking a toll on performance, we can consider moving it
 * to be part of the tree creation function buildSketchSceneTreeFromPRFile.
 */
function useAllFrames(enabled: boolean = true) {
  const { sketchSceneRootElement } = useInspectorContext()

  return useMemo(() => {
    if (!sketchSceneRootElement || !enabled) {
      return []
    }
    return getAllFrames(sketchSceneRootElement)
  }, [enabled, sketchSceneRootElement])
}
